import {
  type EstimateInput,
  type LogisticsRoute,
  useEstimateCountries,
  useEstimateInternational,
  useLogisticsCategories,
} from "@kupikupi/api";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";

/** Volumetric weight (g): L×W×H(cm) ÷ 6000 × 1000. */
function volumetricGrams(l: number, w: number, h: number): number | null {
  if (!l || !w || !h) return null;
  return Math.round(((l * w * h) / 6000) * 1000);
}

/** Recommended-first, then carriable, then cheapest — mirrors aps-web. */
function sortRoutes(routes: LogisticsRoute[]): {
  sorted: LogisticsRoute[];
  cheapestId: string | null;
} {
  const sorted = [...routes].sort((a, b) => {
    if (a.recommended !== b.recommended) return a.recommended ? -1 : 1;
    if (a.carriable !== b.carriable) return a.carriable ? -1 : 1;
    return (a.totalCostNumber ?? Number.POSITIVE_INFINITY) - (b.totalCostNumber ?? Number.POSITIVE_INFINITY);
  });
  let cheapestId: string | null = null;
  let min = Number.POSITIVE_INFINITY;
  for (const r of routes) {
    if (r.carriable && r.totalCostNumber != null && r.totalCostNumber < min) {
      min = r.totalCostNumber;
      cheapestId = r.routeId;
    }
  }
  return { sorted, cheapestId };
}

/** Emoji icon for route shipping type. */
function routeEmoji(shippingType: string | null, recommended: boolean): string {
  if (recommended) return "✈";
  if (!shippingType) return "📦";
  const t = shippingType.toLowerCase();
  if (t.includes("air")) return "✈";
  if (t.includes("sea") || t.includes("ocean")) return "🚢";
  if (t.includes("land") || t.includes("rail") || t.includes("train")) return "🚆";
  if (t.includes("express") || t.includes("fast")) return "🚀";
  if (t.includes("post") || t.includes("mail")) return "📮";
  return "📦";
}

/** Icon background gradient based on shippingType. */
function iconGradient(shippingType: string | null, recommended: boolean): string {
  if (recommended) return "linear-gradient(135deg,#FFE5DD,#FFD3C2)";
  if (!shippingType) return "linear-gradient(135deg,#FFF8DA,#FFE5A0)";
  const t = shippingType.toLowerCase();
  if (t.includes("air")) return "linear-gradient(135deg,#FFE5DD,#FFD3C2)";
  if (t.includes("sea") || t.includes("ocean")) return "linear-gradient(135deg,#E0F7F1,#B8E8DD)";
  if (t.includes("land") || t.includes("rail") || t.includes("train")) return "linear-gradient(135deg,#E0F7F1,#B8E8DD)";
  if (t.includes("express") || t.includes("fast")) return "linear-gradient(135deg,#FFE0E9,#FFC8D6)";
  if (t.includes("post") || t.includes("mail")) return "linear-gradient(135deg,#FFF8DA,#FFE5A0)";
  return "linear-gradient(135deg,#EEF2FF,#D5DCFF)";
}

export function EstimationPage() {
  const { t } = useTranslation("estimation");

  const countriesQuery = useEstimateCountries("RUB");
  // useLogisticsCategories loaded for future category filter; not shown in mini design.
  useLogisticsCategories("RUB");
  const estimate = useEstimateInternational("RUB");

  const [country, setCountry] = useState("");
  const [weightKg, setWeightKg] = useState("2.5");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Flatten grouped country buckets, dedupe by code.
  const countryOptions = useMemo(() => {
    const groups = countriesQuery.data ?? {};
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    for (const list of Object.values(groups)) {
      for (const c of list) {
        if (seen.has(c.code)) continue;
        seen.add(c.code);
        const label = c.nameRu ?? c.nameEn ?? c.nameCn;
        out.push({ value: c.code, label });
      }
    }
    return out;
  }, [countriesQuery.data]);

  const routes = estimate.data ?? [];
  const { sorted, cheapestId } = useMemo(() => sortRoutes(routes), [routes]);

  const volG = volumetricGrams(Number(length), Number(width), Number(height));
  const actualG = Math.round(Number(weightKg) * 1000) || 0;

  const onCalculate = () => {
    setFormError(null);
    if (!country) {
      setFormError(t("form.err_country"));
      return;
    }
    const weightGrams = Math.round(Number(weightKg) * 1000);
    if (!Number.isFinite(weightGrams) || weightGrams <= 0) {
      setFormError(t("form.err_weight"));
      return;
    }
    const l = Number(length) || 0;
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const input: EstimateInput = {
      country,
      weightGrams,
      lengthCm: l || null,
      widthCm: w || null,
      heightCm: h || null,
      categoryList: [],
      includeUnselectable: true,
    };
    estimate.mutate(input);
  };

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      {/* Hero banner — design: gradient-sky bg + title + subtitle */}
      <div style={{ background: "var(--gradient-sky)", padding: "18px 16px", textAlign: "center" }}>
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: 0,
            letterSpacing: "-0.015em",
          }}
        >
          {t("mini.hero_title")}
        </h1>
        <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "6px 0 0" }}>
          {t("mini.hero_sub")}
        </p>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Input card */}
        <div className="tg-card">
          {/* Destination country */}
          <div className="e-field" style={{ marginBottom: 8 }}>
            <label className="e-field__l" style={{ fontSize: 11 }}>
              {t("form.destination")}
            </label>
            {countriesQuery.isLoading ? (
              <TgSkeleton height={38} radius={8} />
            ) : (
              <select
                className="e-select"
                style={{ height: 38, fontSize: 13, width: "100%" }}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="">{t("form.destination_placeholder")}</option>
                {countryOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* L / W / H */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            <div className="e-field">
              <label className="e-field__l" style={{ fontSize: 10 }}>
                {t("form.length")}
              </label>
              <input
                className="e-input"
                value={length}
                inputMode="decimal"
                placeholder="cm"
                style={{ height: 36, fontSize: 12 }}
                onChange={(e) => setLength(e.target.value)}
              />
            </div>
            <div className="e-field">
              <label className="e-field__l" style={{ fontSize: 10 }}>
                {t("form.width")}
              </label>
              <input
                className="e-input"
                value={width}
                inputMode="decimal"
                placeholder="cm"
                style={{ height: 36, fontSize: 12 }}
                onChange={(e) => setWidth(e.target.value)}
              />
            </div>
            <div className="e-field">
              <label className="e-field__l" style={{ fontSize: 10 }}>
                {t("form.height")}
              </label>
              <input
                className="e-input"
                value={height}
                inputMode="decimal"
                placeholder="cm"
                style={{ height: 36, fontSize: 12 }}
                onChange={(e) => setHeight(e.target.value)}
              />
            </div>
          </div>

          {/* Actual weight */}
          <div className="e-field" style={{ marginTop: 8 }}>
            <label className="e-field__l" style={{ fontSize: 11 }}>
              {t("form.weight")}
            </label>
            <div className="e-input-wrap">
              <span className="e-input-prefix">kg</span>
              <input
                className="e-input e-input--with-prefix"
                value={weightKg}
                inputMode="decimal"
                style={{ height: 38, fontSize: 13 }}
                onChange={(e) => setWeightKg(e.target.value)}
              />
            </div>
          </div>

          {/* Volumetric weight hint */}
          {volG != null && (
            <p style={{ fontSize: 10, color: "var(--color-text-muted)", margin: "4px 0 0" }}>
              {t("mini.vol_weight")}: {(volG / 1000).toFixed(2)} kg
              {volG > actualG && (
                <span style={{ color: "var(--color-cta)", marginLeft: 4 }}>
                  ({t("mini.vol_bigger")})
                </span>
              )}
            </p>
          )}

          {/* Form error */}
          {formError && (
            <p
              role="alert"
              style={{ color: "var(--color-cta)", fontSize: 12, fontWeight: 600, margin: "6px 0 0" }}
            >
              {formError}
            </p>
          )}

          {/* Calculate button */}
          <button
            type="button"
            style={{
              marginTop: 10,
              width: "100%",
              background: "var(--color-primary-fill)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "11px 0",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "var(--font-display)",
              cursor: estimate.isPending ? "default" : "pointer",
              opacity: estimate.isPending ? 0.7 : 1,
            }}
            onClick={onCalculate}
            disabled={estimate.isPending}
          >
            {estimate.isPending ? t("form.calculating") : t("form.submit")}
          </button>
        </div>

        {/* Results section */}
        {estimate.isError && (
          <div
            style={{
              margin: "0 0 8px",
              padding: "10px 12px",
              background: "#FFF0F0",
              borderRadius: 10,
              fontSize: 12,
              color: "var(--color-cta)",
              fontWeight: 600,
            }}
          >
            {t("results.error")}
          </div>
        )}

        {estimate.isSuccess && routes.length === 0 && (
          <div
            style={{
              padding: "12px",
              background: "#fff",
              borderRadius: 10,
              border: "1px solid var(--color-border)",
              fontSize: 12,
              color: "var(--color-text-muted)",
            }}
          >
            {length && width && height ? t("results.empty_oversize") : t("results.empty")}
          </div>
        )}

        {sorted.length > 0 && (
          <>
            {/* Section header — design: .tg-sec-h */}
            <div className="tg-sec-h">
              <span className="tg-sec-h__t">
                {t("results.title", { count: sorted.length })}
              </span>
            </div>

            {/* Route cards — design: grid-template-columns 32px 1fr auto */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sorted.map((r, i) => (
                <RouteCard
                  key={r.routeId}
                  route={r}
                  isRecommended={r.recommended && i === 0}
                  isCheapest={r.routeId === cheapestId && !r.recommended}
                  t={t}
                />
              ))}
            </div>

            {/* Savings tip — design: var(--color-primary-soft) panel */}
            <div
              style={{
                background: "var(--color-primary-soft)",
                borderRadius: 10,
                padding: "10px 12px",
                marginTop: 12,
                fontSize: 11,
                lineHeight: 1.55,
              }}
            >
              <strong style={{ color: "var(--color-primary-fill)" }}>{t("tip.title")}</strong>{" "}
              {t("tip.body")}
            </div>
          </>
        )}
      </div>

      {/* Bottom CTA — design: "注册 → 开始代购" / "Регистрация → начать" */}
      <TgMain>{t("summary.cta")}</TgMain>
    </TgPage>
  );
}

type TranslateFn = ReturnType<typeof useTranslation>["t"];

/** Single route card — mirrors design grid: 32px icon | 1fr info | auto price */
function RouteCard({
  route,
  isRecommended,
  isCheapest,
  t,
}: {
  route: LogisticsRoute;
  isRecommended: boolean;
  isCheapest: boolean;
  t: TranslateFn;
}) {
  const emoji = routeEmoji(route.shippingType, isRecommended);
  const grad = iconGradient(route.shippingType, isRecommended);
  const hasTag = isRecommended || isCheapest;

  return (
    <div
      style={{
        background: isRecommended
          ? "linear-gradient(180deg,var(--color-primary-soft),#fff)"
          : "#fff",
        border: isRecommended
          ? "1.5px solid var(--color-primary-fill)"
          : "1px solid var(--color-border)",
        borderRadius: 10,
        padding: 12,
        display: "grid",
        gridTemplateColumns: "32px 1fr auto",
        gap: 10,
        alignItems: "center",
        position: "relative",
        opacity: route.carriable ? 1 : 0.55,
      }}
    >
      {/* Badge ribbon */}
      {hasTag && (
        <span
          style={{
            position: "absolute",
            top: -8,
            left: 10,
            padding: "2px 8px",
            background: isRecommended ? "var(--color-sticker-yellow)" : "var(--color-primary-fill)",
            color: isRecommended ? "var(--color-sticker-yellow-fg)" : "#fff",
            fontFamily: "monospace",
            fontSize: 9,
            fontWeight: 800,
            borderRadius: 4,
          }}
        >
          {isRecommended ? t("line.recommended") : t("line.cheapest")}
        </span>
      )}

      {/* Icon */}
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: grad,
          display: "grid",
          placeItems: "center",
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {emoji}
      </span>

      {/* Name + ETA */}
      <div style={{ minWidth: 0 }}>
        <strong
          style={{
            fontSize: 12,
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {route.name}
        </strong>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>
          {route.minDeliveredDays != null && route.maxDeliveredDays != null
            ? t("line.eta", { min: route.minDeliveredDays, max: route.maxDeliveredDays })
            : null}
          {!route.carriable && route.rejectReason && (
            <span style={{ color: "var(--color-cta)", marginLeft: 4 }}>
              {route.rejectReason}
            </span>
          )}
        </div>
      </div>

      {/* Price */}
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        {route.carriable && route.totalCostDisplay ? (
          <>
            <div
              style={{
                fontFamily: "-apple-system,system-ui,sans-serif",
                fontSize: isRecommended ? 16 : 14,
                fontWeight: 900,
                color: isRecommended ? "var(--color-primary-fill)" : "inherit",
              }}
            >
              {route.totalCostDisplay} ₽
            </div>
            {route.cargoCostDisplay && route.cargoCostDisplay !== route.totalCostDisplay && (
              <div style={{ fontSize: 9, color: "var(--color-text-muted)" }}>
                {t("line.cargo_only", { value: `${route.cargoCostDisplay} ₽` })}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
            {t("line.no_route")}
          </div>
        )}
      </div>
    </div>
  );
}
